import { useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { EventDialog } from './event-dialog';
import { ExportInviteDialog } from './export-invite-dialog';
import { CalculatedDateNotice, useCalculatedDate } from './procedural-calculated-date';
import { flashEventRow, formatDate, formatDateRange, mapDeltaTypeToCounting } from './procedural-helpers';
import {
  COUNTING_LABEL,
  DateTbdLabel,
  DetailCell,
  DetailsAccordion,
  SourceFileButton,
  UnconfirmedChip,
  formatRelationship,
} from './procedural-row-parts';
import { SourceCitation, sourceRefFromChunks } from './procedural-source-drawer';
import { Permission } from '../../../../../../../../backend/src/auth/permissions/permission';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { useDeleteProceduralEvent } from '@/api/mutations/useDeleteProceduralEvent';
import { PermissionGate } from '@/hooks/useHasPermission';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { ACTIVE_HIGHLIGHT_COLOR, HIGHLIGHT_COLOR } from '@/new/components/pdf-viewer/constants';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { splitDateAndTime } from '@/new/components/timeline-calculator/civil-date';
import { formatTimeZoneLabel } from '@/new/lib/time-zones';
import { useTimelineCalculatorStore } from '@/new/stores/timeline-calculator-store';

interface ProceduralEventRowProps {
  event: ProceduralTimelineEvent;
  eventsById: Record<string, ProceduralTimelineEvent>;
  onOpenSource: (citations: SourceCitation[]) => void;
  /** Null = editable. Non-null disables the edit button and is used as the tooltip text. */
  editDisabledReason: string | null;
  hasPendingUpdate: boolean;
  /** Open the review-suggestions modal (clicking an indicator icon). */
  onReviewClick: () => void;
}

function scrollToEvent(eventId: string) {
  const el = document.getElementById(`procedural-event-${eventId}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  flashEventRow(eventId);
}

export function ProceduralEventRow({
  event,
  eventsById,
  onOpenSource,
  editDisabledReason,
  hasPendingUpdate,
  onReviewClick,
}: ProceduralEventRowProps) {
  const isEditDisabled = editDisabledReason !== null;
  const { matterId } = useParams();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { mutate: deleteEvent, isLoading: isDeleting } = useDeleteProceduralEvent();
  const openCalculator = useTimelineCalculatorStore((s) => s.open);

  const latestChunk = event.sourceChunks?.[event.sourceChunks.length - 1];
  const sourceFileName = latestChunk?.file?.name ?? null;
  const dependents = Object.values(eventsById).filter((e) => e.anchorEventId === event.id);
  const anchorName = event.anchorEventId ? (eventsById[event.anchorEventId]?.name ?? null) : null;
  const relationship = formatRelationship(event.deltaInterval, event.deltaIntervalType, anchorName);
  const dateRangeText = formatDateRange(event.startDateTime, event.endDateTime);

  const handleSourceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const primary = sourceRefFromChunks(event.sourceChunks);
    if (primary === null) return;
    const citations: SourceCitation[] = [{ ref: primary, label: 'Event source', color: ACTIVE_HIGHLIGHT_COLOR }];
    // Also surface the anchor event: a second highlight in the same document,
    // or a document-switcher entry across documents.
    const anchor =
      event.anchorEventId !== null
        ? sourceRefFromChunks(eventsById[event.anchorEventId]?.sourceChunks, primary.fileId)
        : null;
    if (anchor !== null && anchor.chunkId !== primary.chunkId) {
      citations.push({ ref: anchor, label: 'Dependent event source', color: HIGHLIGHT_COLOR });
    }
    onOpenSource(citations);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditOpen(true);
  };

  const anchorEvent = event.anchorEventId ? (eventsById[event.anchorEventId] ?? null) : null;
  const calculatedDate = useCalculatedDate({
    matterId,
    anchorStartDateTime: anchorEvent?.startDateTime ?? null,
    days: event.deltaInterval?.days ?? null,
    deltaIntervalType: event.deltaIntervalType,
    jurisdiction: event.jurisdiction,
    currentStartDateTime: event.startDateTime,
  });

  const openCalc = () => {
    if (!matterId) return;
    const anchorStart = anchorEvent?.startDateTime ?? event.startDateTime;
    openCalculator({
      matterId,
      eventId: event.id,
      eventName: event.name,
      startDate: anchorStart ? new Date(anchorStart) : null,
      days: event.deltaInterval?.days ?? 0,
      counting: mapDeltaTypeToCounting(event.deltaIntervalType, event.jurisdiction),
      timeZone: event.timeZone ?? null,
      endDate: event.endDateTime ? new Date(event.endDateTime) : null,
      startTime: splitDateAndTime(event.startDateTime).time,
      endTime: splitDateAndTime(event.endDateTime).time,
      dependentCount: dependents.length,
    });
  };

  const handleCalculatorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openCalc();
  };

  const handleConfirmDelete = () => {
    if (!matterId) return;
    deleteEvent(
      { matterId, eventId: event.id },
      {
        onSuccess: () => {
          toast.success('Event deleted.');
          setIsDeleteOpen(false);
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to delete event.');
        },
      },
    );
  };

  return (
    <div
      id={`procedural-event-${event.id}`}
      className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 transition-shadow duration-500"
    >
      <div className="flex w-full flex-col gap-2 pr-2 text-left">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2">
            <p className="min-w-0 truncate text-sm text-gray-500">{dateRangeText ?? <DateTbdLabel />}</p>
            {event.anchorEventId !== null && calculatedDate.kind !== 'calculable' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    className="group/calc relative shrink-0 border border-solid border-emerald-200 bg-emerald-50 p-1 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700"
                    onClick={handleCalculatorClick}
                    aria-label={
                      calculatedDate.kind === 'mismatch'
                        ? "Date doesn't match the Wexler calculated date. Open the timeline calculator to review it."
                        : 'Open timeline calculator'
                    }
                  >
                    <Icon className="lucide--calculator" />
                    {calculatedDate.kind === 'mismatch' && (
                      <span className="pointer-events-none absolute -top-1.5 -right-1.5 flex items-center justify-center rounded-full bg-amber-50 p-px text-amber-500 group-hover/calc:bg-amber-100 group-hover/calc:text-amber-600">
                        <Icon className="lucide--triangle-alert text-[11px]" />
                      </span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {calculatedDate.kind === 'mismatch'
                    ? "Date doesn't match the Wexler calculated date. Open the timeline calculator to review it."
                    : 'Open timeline calculator'}
                </TooltipContent>
              </Tooltip>
            )}
            <span className="shrink-0">
              <CalculatedDateNotice state={calculatedDate} onOpenCalculator={openCalc} />
            </span>
          </div>

          <div className="flex min-w-0 items-center gap-2">
            {!event.isConfirmed && (
              <UnconfirmedChip
                onClick={(e) => {
                  e.stopPropagation();
                  onReviewClick();
                }}
              />
            )}
            {sourceFileName && <SourceFileButton fileName={sourceFileName} onClick={handleSourceClick} />}
            {event.startDateTime !== null ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExportOpen(true);
                    }}
                    aria-label="Download calendar file"
                  >
                    <Icon className="lucide--calendar-plus" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Add to calendar</TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="inline-flex cursor-not-allowed">
                    <Button type="button" variant="ghost" size="iconSm" disabled aria-label="Download calendar file">
                      <Icon className="lucide--calendar-plus" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Add a date to this event to download a calendar file.</TooltipContent>
              </Tooltip>
            )}
            <PermissionGate permission={Permission.ProceduralTimelineEdit} uiMode="disabled">
              <Button
                variant="ghost"
                className="-mr-1 p-0 text-gray-400 hover:text-gray-700"
                aria-label="Delete event"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleteOpen(true);
                }}
              >
                <Icon className="lucide--x" />
              </Button>
            </PermissionGate>
          </div>
        </div>

        <div className="-mt-1.5 flex min-w-0 items-center gap-1.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="min-w-0 truncate text-lg text-gray-900">{event.name}</span>
            </TooltipTrigger>
            <TooltipContent>{event.name}</TooltipContent>
          </Tooltip>
          <PermissionGate permission={Permission.ProceduralTimelineEdit} uiMode="disabled">
            {isEditDisabled ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="inline-flex shrink-0 cursor-not-allowed">
                    <Button type="button" variant="ghost" size="iconSm" disabled aria-label="Edit event">
                      <Icon className="lucide--pencil" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{editDisabledReason}</TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    className="shrink-0"
                    onClick={handleEditClick}
                    aria-label="Edit event"
                  >
                    <Icon className="lucide--pencil" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit event</TooltipContent>
              </Tooltip>
            )}
          </PermissionGate>
          {hasPendingUpdate && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="iconSm"
                  className="shrink-0 p-0 text-violet-500 hover:bg-transparent hover:text-violet-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReviewClick();
                  }}
                  aria-label="Review the pending suggested update"
                >
                  <Icon className="lucide--refresh-cw" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>This event has a pending suggested update. Click to review suggestions.</TooltipContent>
            </Tooltip>
          )}
        </div>

        {relationship && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => scrollToEvent(event.anchorEventId!)}
            aria-label="Scroll to the event this depends upon"
            className="-mt-1 gap-1.5 self-start p-0 text-left text-xs font-light text-gray-500 hover:bg-transparent hover:text-gray-700"
          >
            <Icon className="lucide--link shrink-0 text-sky-600" />
            {relationship}
          </Button>
        )}

        <DetailsAccordion
          className="mt-1"
          leading={event.description ? <p className="text-sm text-gray-600">{event.description}</p> : undefined}
        >
          <DetailCell label="Created by">{event.createdBy?.email ?? 'Unknown'}</DetailCell>
          <DetailCell label="Created date">{formatDate(event.createdAt)}</DetailCell>
          <DetailCell label="Last updated">{formatDate(event.updatedAt)}</DetailCell>
          <DetailCell label="Time zone">{event.timeZone ? formatTimeZoneLabel(event.timeZone) : 'Not set'}</DetailCell>
          {event.anchorEventId !== null && (
            <DetailCell label="Interval basis">{COUNTING_LABEL[event.deltaIntervalType]}</DetailCell>
          )}
        </DetailsAccordion>
      </div>

      {matterId && (
        <EventDialog
          event={event}
          eventsById={eventsById}
          matterId={matterId}
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
        />
      )}
      <ExportInviteDialog event={event} open={isExportOpen} onOpenChange={setIsExportOpen} />

      <ConfirmationModal
        isOpen={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title="Delete event"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        confirmVariant={'destructive'}
      >
        <p className="text-sm text-gray-700">
          Delete <span className="font-medium">{event.name}</span>? This action cannot be undone.
        </p>
        {dependents.length > 0 && (
          <p className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <Icon className="lucide--triangle-alert mt-0.5 shrink-0 text-amber-600" />
            <span>
              {dependents.length === 1
                ? `“${dependents[0].name}” is calculated based on this event. Deleting will break that relationship.`
                : `${dependents.length} events are calculated based on this event. Deleting will break those relationships.`}
            </span>
          </p>
        )}
      </ConfirmationModal>
    </div>
  );
}
